/**
 * MediaStream Recording API - utilities to record audio from the browser
 */
import { MEDIA_RECORDER_STATES } from 'js/src/components/app/recorder/types';
import { Toast } from 'js/src/components/common/Toast';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Stopwatch from 'js/src/libs/stopwatch';
import { validateInfinityDuration } from 'js/src/libs/video-duration-tools';
import { isError } from 'js/src/types';

/**
 * Supported MIME types that work with MediaRecorder
 */
export enum MEDIA_RECORDER_AUDIO_MIME_TYPES {
    CHROME_OPERA_AUDIO_MIME_TYPE = 'audio/webm',
    FIREFOX_AUDIO_MIME_TYPE = 'audio/ogg',
    SAFARI_AUDIO_MIME_TYPE = 'audio/mp4',
}
export enum MEDIA_RECORDER_VIDEO_MIME_TYPES {
    CHROME_VIDEO_MIME_TYPE = 'video/webm',
    FIREFOX_VIDEO_MIME_TYPE = 'video/webm', // eslint-disable-line @typescript-eslint/no-duplicate-enum-values
    SAFARI_VIDEO_MIME_TYPE = 'video/mp4',
}

export enum RECORDING_OPTIONS {
    AUDIO = 'audio',
    VIDEO = 'video',
    SCREEN = 'screen',
}

export function getDefaultAudioType(): MEDIA_RECORDER_AUDIO_MIME_TYPES | null {
    if (window?.MediaRecorder?.isTypeSupported('audio/ogg;codecs=opus')) {
        return MEDIA_RECORDER_AUDIO_MIME_TYPES.FIREFOX_AUDIO_MIME_TYPE;
    } else if (window?.MediaRecorder?.isTypeSupported('audio/webm;codecs=opus')) {
        return MEDIA_RECORDER_AUDIO_MIME_TYPES.CHROME_OPERA_AUDIO_MIME_TYPE;
    } else if (window?.MediaRecorder?.isTypeSupported('audio/mp4')) {
        return MEDIA_RECORDER_AUDIO_MIME_TYPES.SAFARI_AUDIO_MIME_TYPE;
    } else {
        return null;
    }
}

export function getDefaultVideoType(): MEDIA_RECORDER_VIDEO_MIME_TYPES | null {
    if (
        window?.MediaRecorder?.isTypeSupported(
            MEDIA_RECORDER_VIDEO_MIME_TYPES.CHROME_VIDEO_MIME_TYPE,
        )
    ) {
        return MEDIA_RECORDER_VIDEO_MIME_TYPES.CHROME_VIDEO_MIME_TYPE;
    } else if (
        window?.MediaRecorder?.isTypeSupported(
            MEDIA_RECORDER_VIDEO_MIME_TYPES.FIREFOX_VIDEO_MIME_TYPE,
        )
    ) {
        return MEDIA_RECORDER_VIDEO_MIME_TYPES.FIREFOX_VIDEO_MIME_TYPE;
    } else if (
        window?.MediaRecorder?.isTypeSupported(
            MEDIA_RECORDER_VIDEO_MIME_TYPES.SAFARI_VIDEO_MIME_TYPE,
        )
    ) {
        return MEDIA_RECORDER_VIDEO_MIME_TYPES.SAFARI_VIDEO_MIME_TYPE;
    } else {
        return null;
    }
}

/**
 * Checks to see if the relevant browser APIs are available
 * WIll also return false if not on a https connection
 *
 * @return {boolean}
 */
export function isMediaRecorderSupported(recordingType = RECORDING_OPTIONS.AUDIO) {
    const isTypeSupported =
        recordingType === RECORDING_OPTIONS.AUDIO
            ? getDefaultAudioType() !== null
            : getDefaultVideoType !== null;

    return (
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === 'function' &&
        typeof MediaRecorder === 'function' &&
        isTypeSupported
    );
}

type RecorderOptions = {
    recordingType: RECORDING_OPTIONS;
    useCanvas?: boolean;
    mirrorVideo?: boolean;
};

type SimplifiedMediaDeviceInfo = {
    label: string;
    id: string;
};

type GetAudioInputOptions = {
    includeDefault?: boolean;
};

const CANVAS_FEED_FPS = 30; // fps option when calling canvas.captureStream()
/**
 * An async API for capturing video and/or audio from device inputs.
 */
export class Recorder {
    _recordingType: RECORDING_OPTIONS;
    _useCanvas: boolean;
    _mirrorVideo: boolean;
    _recorder: MediaRecorder | null = null;
    _recordedBlob: Blob[] = [];
    _deviceStream: MediaStream | null = null;
    _stream: MediaStream | null = null;
    _stream_history: MediaStream[] = [];
    _audioType: MEDIA_RECORDER_AUDIO_MIME_TYPES | null = null;
    _audioBlob: Blob | null = null;
    _mediaBlob: Blob | null = null;
    _mediaType: MEDIA_RECORDER_VIDEO_MIME_TYPES | null = null;
    _preCanvasVideoElm: HTMLVideoElement | null = null;
    _internalClock: Stopwatch = new Stopwatch();
    _nextAnimationFrameId: number | null = null;

    constructor(opts?: RecorderOptions) {
        // options
        this._recordingType = opts?.recordingType || RECORDING_OPTIONS.AUDIO;
        this._useCanvas = opts?.useCanvas ?? false;
        this._mirrorVideo = (opts?.mirrorVideo && this._useCanvas) ?? false;

        if (!isMediaRecorderSupported(this._recordingType)) {
            return;
        }

        this._recorder = null;
        this._recordedBlob = [];
        this._deviceStream = null;
        this._stream = null;
        this._stream_history = [];

        this._audioType = getDefaultAudioType();
        this._audioBlob = null;
        this._mediaBlob = null;
        this._mediaType = getDefaultVideoType();
        this._preCanvasVideoElm = null;
        this._nextAnimationFrameId = null;

        this._internalClock = new Stopwatch();
    }

    get fileExtension() {
        if (this._recordingType === RECORDING_OPTIONS.AUDIO) {
            if (this._audioType === MEDIA_RECORDER_AUDIO_MIME_TYPES.CHROME_OPERA_AUDIO_MIME_TYPE) {
                return 'webm';
            } else if (
                this._audioType === MEDIA_RECORDER_AUDIO_MIME_TYPES.FIREFOX_AUDIO_MIME_TYPE
            ) {
                return 'ogg';
            } else if (this._audioType === MEDIA_RECORDER_AUDIO_MIME_TYPES.SAFARI_AUDIO_MIME_TYPE) {
                return 'mp4';
            } else {
                throw new Error(
                    'No applicable audio type was found. No file extension could be provided.',
                );
            }
        } else if (
            this._recordingType === RECORDING_OPTIONS.VIDEO ||
            this._recordingType === RECORDING_OPTIONS.SCREEN
        ) {
            // all browsers support mp4 playback
            return 'mp4';
        } else {
            throw new Error('Recording type was undefined.');
        }
    }

    get audioBlob() {
        return this._audioBlob;
    }

    get audioType() {
        return this._audioType;
    }

    get mediaBlob() {
        return this._mediaBlob;
    }

    get state() {
        if (this._recorder) {
            if (this._recorder.state === MEDIA_RECORDER_STATES.INACTIVE) {
                return MEDIA_RECORDER_STATES.INACTIVE;
            }
            if (this._recorder.state === MEDIA_RECORDER_STATES.RECORDING) {
                return MEDIA_RECORDER_STATES.RECORDING;
            }
            if (this._recorder.state === MEDIA_RECORDER_STATES.PAUSED) {
                return MEDIA_RECORDER_STATES.PAUSED;
            }
        }

        return MEDIA_RECORDER_STATES.INACTIVE; // default state
    }

    get recordedTime() {
        return this._internalClock.totalDuration;
    }

    /**
     * Enumerates all audio devices that are available on the client's machine
     * For Chrome/Opera, omits the "default" option (which is duplicated by the deviceId)
     * {@see {@link https://stackoverflow.com/questions/53304934/why-does-mediadevices-enumeratedevices-list-some-devices-twice-what-is-default}
     *
     * @return {promise} resolves {[MediaDeviceInfo]}, a filtered list containing only audio devices
     */
    async getAudioInputs(opts?: GetAudioInputOptions) {
        return new Promise<SimplifiedMediaDeviceInfo[]>((resolve, reject) => {
            navigator.mediaDevices
                .enumerateDevices()
                .then((deviceInfos) => {
                    if (deviceInfos.length === 1 && !deviceInfos[0].deviceId) {
                        reject(Error('Recorder permissions are required to get audio inputs.'));
                    }

                    // options
                    const { includeDefault = false } = opts || {};

                    let audioOptions = deviceInfos
                        .filter((device) => device.kind === 'audioinput')
                        .map((device) => ({
                            label: device.label,
                            id: device.deviceId,
                        }));

                    if (!includeDefault) {
                        audioOptions = audioOptions.filter((device) => device.id !== 'default');
                    }

                    resolve(audioOptions);
                })
                .catch((e: unknown) => {
                    ErrorReporter.capture(e);
                    reject(e);
                });
        });
    }

    async getVideoInputs() {
        return new Promise<SimplifiedMediaDeviceInfo[]>((resolve, reject) => {
            navigator.mediaDevices
                .enumerateDevices()
                .then((deviceInfos) => {
                    if (deviceInfos.length === 1 && !deviceInfos[0].deviceId) {
                        reject(Error('Recorder permissions are required to get video inputs.'));
                    }
                    const videoOptions = deviceInfos
                        .filter((device) => device.kind === 'videoinput')
                        .map((device) => ({
                            label: device.label,
                            id: device.deviceId,
                        }));

                    resolve(videoOptions);
                })
                .catch((e: unknown) => {
                    ErrorReporter.capture(e);
                    reject(e);
                });
        });
    }

    /**
     * Sets up the recorder from scratch with either the default input marked by
     * the browser or for a specific input
     *
     * @param {object} deviceIds optional, a specific deviceId to create a mediastream for
     * @return {promise} resolves a MediaStream instance from navigator.mediaDevices.getUserMedia()
     */
    async setup(deviceIds: MediaStreamConstraints) {
        if (this.audioType === null) {
            Toast.error(
                'Your browser does not support recording audio. Please use a different browser.',
            );
            throw new Error(
                'Your browser does not support recording audio. Please use a different browser.',
            );
        }
        if (this._mediaType === null) {
            Toast.error(
                'Your browser does not support recording video. Please use a different browser.',
            );
            throw new Error(
                'Your browser does not support recording video. Please use a different browser.',
            );
        }

        this.reset();

        const audioInputId = (deviceIds?.audio as MediaTrackConstraints)?.deviceId;
        const videoInputId = (deviceIds?.video as MediaTrackConstraints)?.deviceId;

        const audioConstraint: MediaTrackConstraints | boolean = audioInputId
            ? { deviceId: audioInputId }
            : true;

        const videoConstraint: MediaTrackConstraints = videoInputId
            ? {
                  deviceId: videoInputId,
                  width: { ideal: 1920 },
                  height: { ideal: 1080 },
              }
            : {
                  width: { ideal: 1920 },
                  height: { ideal: 1080 },
              };

        // if voiceover, just record audio, else record both audio/video
        const constraints: MediaStreamConstraints =
            this._recordingType === RECORDING_OPTIONS.AUDIO ||
            this._recordingType === RECORDING_OPTIONS.SCREEN
                ? {
                      audio: audioConstraint,
                  }
                : {
                      audio: audioConstraint,
                      video: videoConstraint,
                  };
        return new Promise<MediaStream>((resolve, reject) => {
            navigator.mediaDevices
                .getUserMedia(constraints)
                .then(async (stream) => {
                    this._deviceStream = stream;
                    let mediaStream = stream; // default stream - use device inputs
                    this._stream_history.push(mediaStream); // keep track of our recording streams so we can close them later

                    // we have the option to feed the camera stream to a hidden <video/> and draw onto a <canvas/>
                    // this allows us to modify the camera feed (ie. mirror the camera feed)
                    if (this._useCanvas) {
                        let sourceVideoStream: MediaStream | null = null;
                        if (this._recordingType === RECORDING_OPTIONS.VIDEO) {
                            sourceVideoStream = await this._createCanvasStream(stream);
                        } else if (this._recordingType === RECORDING_OPTIONS.SCREEN) {
                            sourceVideoStream = await navigator.mediaDevices.getDisplayMedia({
                                video: true,
                            });
                        } else {
                            throw new Error('not even recording video, you cannot use canvas!');
                        }
                        const audioStream = stream.getAudioTracks()[0];
                        const videoStream = sourceVideoStream.getVideoTracks()[0];
                        const mixedStream = new MediaStream([videoStream, audioStream]);

                        this._stream_history.push(stream); // keep track of the original camera stream so we can close it later
                        mediaStream = mixedStream;
                    }

                    if (mediaStream) {
                        // setup recorder
                        this._recorder = new MediaRecorder(mediaStream);
                        this._recorder.ondataavailable = (e: BlobEvent) => {
                            if (e.data.size > 0) {
                                this._recordedBlob.push(e.data);
                            }
                        };
                        this._recorder.onerror = (e: Event) => {
                            throw e;
                        };

                        // if it's canvas, stream is canvas + audio, otherwise stream is from getUserMedia()
                        resolve(mediaStream);
                    }
                })
                .catch((e: unknown) => {
                    let reportError = true;

                    // silence errors that we can't do anything about
                    if (isError(e) && e.name === 'NotAllowedError') {
                        // user blocked us, we show a screenshot to the user to unblock us in the modal
                        reportError = false;
                    }
                    if (isError(e) && e.name === 'NotFoundError') {
                        // user has no camera/microphone, we can't do anything about it
                        reportError = false;
                        Toast.error(
                            'No camera or microphone found. Please connect a camera or microphone and try again.',
                        );
                    } else if (isError(e) && e.name === 'NotReadableError') {
                        // user hardware issues, can't do anything about it
                        reportError = false;
                        if (/Device\sin\suse/gi.test(e.message)) {
                            Toast.error(
                                'The camera or microphone is already in use. Please close any other applications using the camera or microphone and try again.',
                            );
                        }
                    }

                    // report unknown errors to sentry
                    if (reportError) {
                        ErrorReporter.capture(e);
                    }
                    reject(e);
                });
        });
    }

    /**
     * Overwrites any existing chunks and starts writing data
     *
     * @return {promise} resolves when the recorder successfully starts
     */
    async startRecording() {
        if (!this._recorder) {
            throw new Error('The recording device needs to be set up first.');
        }

        return new Promise((resolve, reject) => {
            if (!this._recorder) {
                reject(new Error('The recording device needs to be set up first.'));
                return;
            }
            this._recordedBlob = [];
            this._recorder.onstart = (e: Event) => {
                resolve(e);
            };
            // SAFARI BUG: in the spec, the argument to MediaRecorder.start() is optional.
            // But if we don't supply one, Safari produces files that the backend thinks are only
            // (just under) one second in duration. ffprobe cannot find the correct duration of these
            // broken files. Supplying a timeSlice of 1000ms fixes this issue.
            // https://community.openai.com/t/whisper-problem-with-audio-mp4-blobs-from-safari/322252
            this._recorder.start(1000);
            this._internalClock.start();
        });
    }

    pauseRecording() {
        if (!this._recorder) {
            throw new Error('The recording device needs to be set up first.');
        }

        if (this._recorder.state !== MEDIA_RECORDER_STATES.INACTIVE) {
            this._recorder.pause();
            this._internalClock.pause();
        } else {
            // silently fail for now (MediaRecorder will throw an error, which should notify sentry)
        }
    }

    resumeRecording() {
        if (!this._recorder) {
            throw new Error('The recording device needs to be set up first.');
        }

        if (this._recorder.state !== MEDIA_RECORDER_STATES.INACTIVE) {
            this._recorder.resume();
            this._internalClock.start();
        } else {
            // silently fail for now (MediaRecorder will throw an error, which should notify sentry)
        }
    }

    /**
     * Stops the recorder from writing data
     *
     * @return {promise} Resolves when all available streams are stopped and a new blob is created
     *  with the recorder contents
     */
    async stopRecording() {
        if (!this._recorder) {
            throw new Error('The recording device needs to be set up first.');
        }

        return new Promise<void>((resolve, reject) => {
            if (!this._recorder) {
                reject(new Error('The recording device needs to be set up first.'));
                return;
            }
            this._recorder.onstop = (e: Event) => {
                if (this._audioType === null) {
                    reject(
                        new Error(
                            'Your browser does not support recording audio. Please use a different browser.',
                        ),
                    );
                    return;
                }
                if (this._mediaType === null) {
                    reject(
                        new Error(
                            'Your browser does not support recording video. Please use a different browser.',
                        ),
                    );
                    return;
                }
                if (!this._recordedBlob.length) {
                    reject(new Error('No data was captured during recording.'));
                    return;
                }
                this._stopStreams();

                if (this._recordingType === RECORDING_OPTIONS.AUDIO) {
                    this._audioBlob = new Blob(this._recordedBlob, {
                        type: this._audioType,
                    });
                }
                if (
                    this._recordingType === RECORDING_OPTIONS.VIDEO ||
                    this._recordingType === RECORDING_OPTIONS.SCREEN
                ) {
                    this._mediaBlob = new Blob(this._recordedBlob, {
                        type: this._mediaType,
                    });
                }

                resolve();
            };
            this._recorder.stop();
            this._internalClock.stop();
        });
    }

    /**
     * Resets the media recorder instance to setup from scratch again
     */
    reset() {
        this._audioBlob = null;
        this._mediaBlob = null;
        this._recorder = null;
        this._recordedBlob = [];
        this._stopStreams();
        this._internalClock && this._internalClock.reset();
    }

    // if any enabled streams, stop them
    _stopStreams() {
        if (this._stream && this._stream.getTracks().length) {
            this._stream.getTracks().forEach((stream) => {
                stream.stop(); // stop all streams to be sure
            });
        }
        this._stream = null;
        this._deviceStream = null;

        // a stream is created for every .getUserMedia(), the caller may have initialized multiple streams if
        // setup() is called multiple times. this ensures all streams created with this instance calls .stop()
        // source: https://stackoverflow.com/questions/44274410/mediarecorder-stop-doesnt-clear-the-recording-icon-in-the-tab
        // (look at all the comments in the accepted answer)
        this._stream_history &&
            this._stream_history.length &&
            this._stream_history.forEach((stream) => {
                if (stream && stream.getTracks().length) {
                    stream.getTracks().forEach((streamTrack) => streamTrack.stop());
                }
            });

        // cleanup the requestAnimationCallbacks
        this._cancelAnimationFrame();
        this._cleanupCanvasStream();
    }

    _cleanupCanvasStream() {
        if (this._preCanvasVideoElm) {
            this._preCanvasVideoElm.remove();
            this._preCanvasVideoElm = null;
        }
    }
    /**
     * Feeds the stream into a <canvas/> where we can modify the video feed dynamically.
     * For example, this is used to flip the webcam feed horizontally because the native feed is mirrored.
     *
     * @param {MediaStream} stream - original stream from getUserMedia()
     * @returns {MediaStream} canvasStream - returns a stream where video is modified in a <canvas/>
     */
    _createCanvasStream(stream: MediaStream) {
        if (!this._useCanvas) {
            return stream;
        }
        return new Promise<MediaStream>((resolve, reject) => {
            this._cleanupCanvasStream();
            this._preCanvasVideoElm = document.createElement('video');
            this._preCanvasVideoElm.playsInline = true;
            this._preCanvasVideoElm.autoplay = true; // required in order for <canvas/> to successfully capture <video/>
            this._preCanvasVideoElm.muted = true;
            const canvasElm = document.createElement('canvas');
            this._preCanvasVideoElm.srcObject = stream;

            this._preCanvasVideoElm.addEventListener('playing', () => {
                if (this._preCanvasVideoElm === null) {
                    reject(new Error('The canvas video element was not found.'));
                    return;
                }
                canvasElm.width = this._preCanvasVideoElm.videoWidth;
                canvasElm.height = this._preCanvasVideoElm.videoHeight;

                const ctx = canvasElm.getContext('2d');
                if (!ctx) {
                    reject(new Error('The canvas context was not found.'));
                    return;
                }

                if (this._mirrorVideo) {
                    ctx.translate(canvasElm.width, 0);
                    ctx.scale(-1, 1);
                }

                function drawCanvas(this: Recorder) {
                    if (!ctx) {
                        throw new Error('The canvas context was not found.');
                    }
                    if (!this._preCanvasVideoElm)
                        throw new Error('The canvas video element was not found.');
                    ctx.drawImage(this._preCanvasVideoElm, 0, 0, canvasElm.width, canvasElm.height);
                    this._requestAnimationFrame(drawCanvas);
                }
                // our stepping criteria to recursively draw on canvas from <video/> frame
                this._nextAnimationFrameId = window.requestAnimationFrame(drawCanvas.bind(this));
                const canvasStream = canvasElm.captureStream(CANVAS_FEED_FPS);
                resolve(canvasStream);
            });

            this._preCanvasVideoElm.play();

            // testing this, we realized that Chrome makes the video super laggy if the this._preCanvasVideoElm
            // is not in the DOM, and visible. We tried turning the opacity to 0, positioning the
            // video offscreen, etc. But the only thing that makes the performance good is making
            // it actually visible. So we make a 1px X 1px video in the top corner of the screen.
            this._preCanvasVideoElm.style.width = '1px';
            this._preCanvasVideoElm.style.height = '1px';
            this._preCanvasVideoElm.style.position = 'absolute';
            this._preCanvasVideoElm.style.zIndex = '9999999999999';
            document.body.appendChild(this._preCanvasVideoElm);
        });
    }

    // animation frame management credits to:
    // https://stackoverflow.com/questions/63866996/cancel-the-requestanimationframe-loop-after-some-time
    _requestAnimationFrame(callback: FrameRequestCallback) {
        if (typeof callback !== 'function') return;

        if (this._nextAnimationFrameId) {
            // only request for the next frame if we have one to render
            this._nextAnimationFrameId = window.requestAnimationFrame(callback.bind(this));
        }
    }
    _cancelAnimationFrame() {
        if (this._nextAnimationFrameId !== null) {
            window.cancelAnimationFrame(this._nextAnimationFrameId); // cancel our current animation
        }
        // condition for stepping so we're stopping the rendering loop, check _requestAnimationFrame()
        this._nextAnimationFrameId = null;
    }
}

/**
 * Get the duration of a blob: to address Chromium bug re: seekable chunks see {@link validateInfinityDuration}
 * @resolve {number} the duration, in seconds
 */
export async function getBlobDuration(blob: string | Blob | MediaSource) {
    const tempVideoEl = document.createElement('video');

    const durationP = new Promise<number>((resolve) =>
        tempVideoEl.addEventListener('loadedmetadata', async () => {
            await validateInfinityDuration(tempVideoEl);
            const duration = tempVideoEl.duration;
            tempVideoEl.currentTime = 0; // reset the currentTime to 0
            window.URL.revokeObjectURL(tempVideoEl.src);
            resolve(duration);
        }),
    );

    tempVideoEl.src = typeof blob === 'string' ? blob : window.URL.createObjectURL(blob);

    return durationP;
}
